import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { datePickerSharedProps } from '@/common/utils/date-picker';
import {
  FloatingDateField,
  type IFloatingDateFieldProps,
} from '@coverflex-tech/hypernova';

interface IProps<T extends FieldValues>
  extends Omit<
    IFloatingDateFieldProps,
    'id' | 'onChange' | 'value' | 'i18nKeys' | 'formatMonth' | 'formatWeekDay'
  > {
  name: Path<T>;
  control?: Control<T>;
}

export const ControlledDateField = <T extends FieldValues>({
  name,
  control,
  ...restProps
}: IProps<T>) => {
  const { t } = useTranslation();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...field }, fieldState }) => {
        if (restProps.variant === 'range') {
          return (
            <FloatingDateField
              {...field}
              {...restProps}
              id={name}
              variant="range"
              value={field.value ?? null}
              onChangeRange={(_, dates) => {
                field.onChange(dates);
              }}
              onChange={undefined}
              error={
                fieldState.error?.message
                  ? t(fieldState.error.message)
                  : undefined
              }
              {...datePickerSharedProps}
            />
          );
        }

        return (
          <FloatingDateField
            {...field}
            {...restProps}
            id={name}
            variant="single"
            value={field.value ?? null}
            onChange={(_, date) => {
              field.onChange(date);
            }}
            onChangeRange={undefined}
            error={
              fieldState.error?.message
                ? t(fieldState.error.message)
                : undefined
            }
            {...datePickerSharedProps}
          />
        );
      }}
    />
  );
};
